import moment from 'moment';
import 'moment/locale/fi';
import { LOCALE_FIN } from '@/lib/helpers/i18n';

// TODO: change to dayjs
import dayjs, { type Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(isoWeek);
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export function parseTimestampToLocale(value: string) {
  moment.locale('fi');
  return moment(value).format('lll');
}

export function isDateValid(date: Date | null): boolean {
  if (!date) {
    return false;
  }
  return !isNaN(date.getTime());
}

export function isDateStrValid(dateStr: string | null): boolean {
  if (!dateStr) {
    return false;
  }
  return !isNaN(new Date(dateStr).getTime());
}

/**
 * Calculates a date for the first Monday of a given week number and year.
 *
 * @param {number} week Week number
 * @param {number} year Year
 * @returns {Date} Date object
 */
export function getDateOfWeek(week: number, year: number) {
  // Create a date for 1 Jan in required year
  const date = new Date(year, 0, 1);
  // Get day of week number, sun = 0, mon = 1, etc.
  const dayNum = date.getDay();

  // Get days to add
  let requiredDate = --week * 7;

  // For ISO week numbering
  // If 1 Jan is Friday to Sunday, go to next week
  if (dayNum > 4) {
    requiredDate += 7;
  }

  // Add required number of days
  date.setDate(1 - date.getDay() + ++requiredDate);

  return dayjs(date).format('YYYYMMDD');
}

/**
 * Formats a single date to a week of format yyyyww (e.g. 202403)
 *
 * @param {mixed} date - Date to be formatted, as a string or Date object
 * @returns {string} - e.g. 202403 or 202335, or empty string for invalid dates.
 */
export function formatDateToWeek(date: string | Date) {
  if (!date) return '';

  if (typeof date === 'string') {
    date = new Date(date);
  }

  if (!(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }

  let year = date.getFullYear();
  const week = moment(date).format('WW');

  // Handle first week of the year. Often the first week of the year starts already at the end of the previous year.
  // In that case, we need to increment the year by one, otherwise a week starting 2019-12-31 might become 201901 instead of 202001.
  if (week === '01' && date.getMonth() === 11) {
    year++;
  }

  return `${year}${week}`;
}

/**
 * Checks whether a week is inside a date range.
 * @param {string} week Week of format yyyyww (e.g. 202403)
 * @param {string|Date} startDate Start date of format yyyy-mm-dd or Date object
 * @param {string|Date} endDate End date of format yyyy-mm-dd or Date object
 * @returns {boolean} True if week is inside date range, false otherwise
 */
export function weekIsInsideDateRange(
  week: string,
  startDate: string | Date,
  endDate: string | Date
) {
  // Format weeks to number of format yyyyww (e.g. 202301) for comparison
  const startDateWeek = parseInt(formatDateToWeek(startDate));
  const endDateWeek = parseInt(formatDateToWeek(endDate));
  const weekInt = parseInt(week);

  // Check if values are valid numbers
  if (isNaN(startDateWeek) || isNaN(endDateWeek)) {
    // No valid ship dates, return all delivery weeks
    return true;
  }

  return !isNaN(weekInt) && weekInt >= startDateWeek && weekInt <= endDateWeek;
}

/**
 * Formats a delivery date to a readable string.
 *
 * @param {(string|Date)} deliveryDate - Delivery date as a string (20250101) or Date object.
 * @returns {string} Formatted date string in the format dd.mm.yyyy or an empty string for invalid dates.
 */
export function formatDeliveryDate(deliveryDate: string | Date) {
  if (!deliveryDate) return '';

  if (typeof deliveryDate === 'string') {
    const day = +deliveryDate.slice(6, 8);
    const month = +deliveryDate.slice(4, 6);
    const year = +deliveryDate.slice(0, 4);
    return day && month && year ? `${day}.${month}.${year}` : '';
  }

  if (deliveryDate instanceof Date) {
    const day = deliveryDate.getDate();
    const month = deliveryDate.getMonth() + 1;
    const year = deliveryDate.getFullYear();
    return day && month && year ? `${day}.${month}.${year}` : '';
  }

  return '';
}

/**
 * Formats a date to a readable string.
 *
 * @param date - Date to be formatted, as a string or Date object
 * @param parseFormat - Optional format string (YYYYMMDD for example) for parsing the date.
 */
export function getIsoWeekFormatted(date: string | Date | Dayjs, parseFormat?: string) {
  if (!date) {
    return '';
  }
  try {
    const parsedDate = parseFormat ? dayjs(date, parseFormat) : dayjs(date);
    return `${LOCALE_FIN.WEEK_SHORT}\u00A0${getIsoWeek(date, parseFormat)},\u00A0${parsedDate.format('YYYY')}`;
  } catch {
    return '';
  }
}

/**
 * Returns the ISO week number of a given date.
 *
 * @param date - Date to be formatted, as a string or Date object
 * @param parseFormat - Optional format string (YYYYMMDD for example) for parsing the date.
 */
export function getIsoWeek(date: string | Date | Dayjs, parseFormat?: string) {
  if (!date) {
    return '';
  }

  try {
    const parsedDate =
      parseFormat && typeof date === 'string' ? dayjs(date, parseFormat) : dayjs(date);
    return String(parsedDate.isoWeek());
  } catch {
    return '';
  }
}

/**
 * Converts delivery date to legacy week format
 *
 * Expects date in 'YYYYMMDD' format (e.g. '20240315' for March 15, 2024).
 * Returns week in 'YYYYWW' format where WW is the ISO week number (e.g. '202411').
 * Returns the input as is if it's already in 'YYYYWW' format.
 *
 * @param {string | null} deliveryDate - Date in 'YYYYMMDD' format or week in 'YYYYWW' format
 * @returns {string} Week number in 'YYYYWW' format, or empty string for invalid inputs
 */
export function convertDeliveryDateToDeliveryWeek(deliveryDate: string | null) {
  if (!deliveryDate) {
    return '';
  }

  if (/^\d{6}$/.test(deliveryDate)) {
    return deliveryDate;
  }

  if (!/^\d{8}$/.test(deliveryDate)) {
    return '';
  }

  const date = dayjs(deliveryDate, 'YYYYMMDD');

  if (!date.isValid()) {
    return '';
  }

  const week = date.isoWeek();
  const year = date.isoWeekYear();
  const weekStr = week.toString().padStart(2, '0');

  return `${year}${weekStr}`;
}

/**
 * Returns the start and end dates for a given season string.
 *
 * The season string should be in the format `<prefix>/<year>`, where `<prefix>` is either 'KK' or 'ST'.
 * - For 'KK' (e.g., 'KK/2024'), the date range is from December 1st of the previous year to September 30th of the given year.
 * - For 'ST' (e.g., 'ST/2024'), the date range is from June 1st of the given year to March 31st of the following year.
 *
 * All dates are calculated in the 'Europe/Helsinki' timezone.
 *
 * @param season - The season string in the format `<prefix>/<year>`.
 * @returns An object containing the `startDate` and `endDate` as JavaScript `Date` objects.
 */
export function getNosDateRange(season: string): { startDate: Date; endDate: Date } {
  const [prefix, yearStr] = season.split('/');

  const year = Number(yearStr);
  let startDate = new Date();
  let endDate = new Date();

  if (!isNaN(year)) {
    switch (prefix) {
      case 'KK':
        startDate = dayjs.tz(`${year - 1}-12-01 00:00:00.000`, 'Europe/Helsinki').toDate();
        endDate = dayjs.tz(`${year}-09-30 23:59:59.999`, 'Europe/Helsinki').toDate();
        break;
      case 'ST':
        startDate = dayjs.tz(`${year}-06-01 00:00:00.000`, 'Europe/Helsinki').toDate();
        endDate = dayjs.tz(`${year + 1}-03-31 23:59:59.999`, 'Europe/Helsinki').toDate();
        break;
    }
  }

  return { startDate, endDate };
}
